import { array, discriminatedUnion, email, enum as enumFactory, literal, number, strictObject, string, url } from "../../lazy-zod.js";
//#region src/schemas/runtime/message-channel.ts
const MESSAGE_CHANNEL_PROVIDERS = [
	"slack",
	"lark",
	"feishu"
];
const MESSAGE_CHANNEL_TRANSPORTS = ["webhook", "websocket"];
const MESSAGE_CHANNEL_EVENT_KINDS = [
	"message",
	"card_action",
	"bot_added",
	"reaction",
	"document_comment"
];
const MESSAGE_CHANNEL_RESOURCE_TYPES = [
	"image",
	"file",
	"audio",
	"video",
	"sticker"
];
const MESSAGE_CHANNEL_MENTION_TYPES = [
	"direct_message",
	"group_mention",
	"group_message",
	"none"
];
const MESSAGE_CHANNEL_EVENT_TEXT_MAX_CHARS = 4e3;
const MESSAGE_CHANNEL_MAX_RESOURCES = 10;
const nonEmptyStringSchema = string().trim().min(1);
const providerIdSchema = nonEmptyStringSchema.max(512);
const SLACK_MESSAGE_TIMESTAMP_PATTERN = /^\d+\.\d+$/u;
const SLACK_THREAD_PERMALINK_PATH_PATTERN = /^\/archives\/([A-Za-z0-9_-]+)\/p(\d{10})(\d{1,6})\/?$/u;
const timestampMsSchema = number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
const slackMessagePermalinkSchema = url().max(2048).refine(isSlackMessagePermalink, { message: "Slack permalinks must use an HTTPS slack.com host." });
const slackThreadPublicationPermalinkSchema = slackMessagePermalinkSchema.refine((value) => parseSlackThreadPublicationPermalink(value) !== null, { message: "Slack thread permalinks must identify an exact Slack channel message." });
function parseSlackThreadPublicationPermalink(value) {
	if (!slackMessagePermalinkSchema.safeParse(value).success) return null;
	const url = new URL(value);
	const pathMatch = SLACK_THREAD_PERMALINK_PATH_PATTERN.exec(url.pathname);
	if (!pathMatch) return null;
	const channelId = pathMatch[1];
	const seconds = pathMatch[2];
	const fractional = pathMatch[3];
	if (!(channelId && seconds && fractional)) return null;
	const queryChannelId = url.searchParams.get("cid");
	if (queryChannelId && queryChannelId !== channelId) return null;
	const queryThreadTs = url.searchParams.get("thread_ts");
	if (queryThreadTs && !SLACK_MESSAGE_TIMESTAMP_PATTERN.test(queryThreadTs)) return null;
	return {
		channelId,
		threadTs: queryThreadTs ?? `${seconds}.${fractional}`
	};
}
function isSlackMessagePermalink(value) {
	let url;
	try {
		url = new URL(value);
	} catch {
		return false;
	}
	const hostname = url.hostname.toLowerCase();
	return url.protocol === "https:" && !url.username && !url.password && (hostname === "slack.com" || hostname.endsWith(".slack.com"));
}
const messageChannelProviderSchema = enumFactory(MESSAGE_CHANNEL_PROVIDERS);
const messageChannelParticipantIdentitySchema = strictObject({
	connectionId: providerIdSchema,
	provider: messageChannelProviderSchema,
	providerUserEmail: email().max(320).optional(),
	providerUserId: providerIdSchema
});
const messageChannelTransportSchema = enumFactory(MESSAGE_CHANNEL_TRANSPORTS);
const messageChannelEventKindSchema = enumFactory(MESSAGE_CHANNEL_EVENT_KINDS);
const messageChannelResourceTypeSchema = enumFactory(MESSAGE_CHANNEL_RESOURCE_TYPES);
const messageChannelMentionTypeSchema = enumFactory(MESSAGE_CHANNEL_MENTION_TYPES);
const slackMessageChannelConversationLocatorSchema = strictObject({
	provider: literal("slack"),
	connectionId: providerIdSchema,
	teamId: providerIdSchema,
	channelId: providerIdSchema,
	rootMessageId: providerIdSchema
});
const createLarkPlatformConversationLocatorSchema = (provider) => strictObject({
	provider: literal(provider),
	connectionId: providerIdSchema,
	appId: providerIdSchema,
	tenantKey: providerIdSchema.nullable(),
	chatId: providerIdSchema,
	rootMessageId: providerIdSchema,
	threadId: providerIdSchema.nullable()
});
const messageChannelConversationLocatorSchema = discriminatedUnion("provider", [
	slackMessageChannelConversationLocatorSchema,
	createLarkPlatformConversationLocatorSchema("lark"),
	createLarkPlatformConversationLocatorSchema("feishu")
]);
const messageChannelResourceDescriptorSchema = strictObject({
	type: messageChannelResourceTypeSchema,
	fileKey: providerIdSchema,
	fileName: nonEmptyStringSchema.max(512).nullable(),
	mimeType: nonEmptyStringSchema.max(256).nullable(),
	sizeBytes: number().int().nonnegative().max(Number.MAX_SAFE_INTEGER).nullable()
});
const messageChannelConversationEventShape = { locator: messageChannelConversationLocatorSchema };
const normalizedMessageChannelMessageEventSchema = strictObject({
	kind: literal("message"),
	messageId: providerIdSchema,
	sentAtMs: timestampMsSchema,
	senderId: providerIdSchema,
	senderDisplayName: nonEmptyStringSchema.max(256).optional(),
	senderTenantKey: providerIdSchema.optional(),
	...messageChannelConversationEventShape,
	text: nonEmptyStringSchema.max(MESSAGE_CHANNEL_EVENT_TEXT_MAX_CHARS),
	mention: messageChannelMentionTypeSchema,
	resources: array(messageChannelResourceDescriptorSchema).max(10)
});
const normalizedMessageChannelCardActionEventSchema = strictObject({
	kind: literal("card_action"),
	actionId: providerIdSchema,
	occurredAtMs: timestampMsSchema,
	senderId: providerIdSchema,
	...messageChannelConversationEventShape,
	actionName: nonEmptyStringSchema.max(128),
	actionValue: nonEmptyStringSchema.max(8192).nullable()
});
const messageChannelCardControlSchema = discriminatedUnion("action", [strictObject({
	v: literal(1),
	action: literal("stop"),
	deliveryId: providerIdSchema
}), strictObject({
	v: literal(1),
	action: literal("reply"),
	deliveryId: providerIdSchema,
	text: string().trim().min(1).max(1e3)
})]);
const normalizedMessageChannelBotAddedEventSchema = strictObject({
	kind: literal("bot_added"),
	chatId: providerIdSchema,
	occurredAtMs: timestampMsSchema,
	addedById: providerIdSchema.nullable()
});
const normalizedMessageChannelReactionEventSchema = strictObject({
	kind: literal("reaction"),
	messageId: providerIdSchema,
	occurredAtMs: timestampMsSchema,
	senderId: providerIdSchema,
	...messageChannelConversationEventShape,
	reaction: nonEmptyStringSchema.max(128),
	operation: enumFactory(["add", "remove"])
});
const normalizedMessageChannelDocumentCommentEventSchema = strictObject({
	kind: literal("document_comment"),
	commentId: providerIdSchema,
	documentToken: providerIdSchema,
	occurredAtMs: timestampMsSchema,
	senderId: providerIdSchema,
	...messageChannelConversationEventShape,
	text: nonEmptyStringSchema.max(MESSAGE_CHANNEL_EVENT_TEXT_MAX_CHARS)
});
const normalizedMessageChannelEventSchema = discriminatedUnion("kind", [
	normalizedMessageChannelMessageEventSchema,
	normalizedMessageChannelCardActionEventSchema,
	normalizedMessageChannelBotAddedEventSchema,
	normalizedMessageChannelReactionEventSchema,
	normalizedMessageChannelDocumentCommentEventSchema
]);
const messageChannelIngressEnvelopeSchema = strictObject({
	v: literal(1),
	kind: literal("remote_agent_message_channel_event"),
	provider: messageChannelProviderSchema,
	connectionId: providerIdSchema,
	providerEventId: providerIdSchema,
	connectionGeneration: number().int().positive().optional(),
	receivedAtMs: timestampMsSchema,
	gatewayRequestId: providerIdSchema,
	event: normalizedMessageChannelEventSchema
}).superRefine((envelope, context) => {
	if (!("locator" in envelope.event)) return;
	if (envelope.event.locator.provider !== envelope.provider) context.addIssue({
		code: "custom",
		message: "Ingress provider must match the conversation locator.",
		path: [
			"event",
			"locator",
			"provider"
		]
	});
	if (envelope.event.locator.connectionId !== envelope.connectionId) context.addIssue({
		code: "custom",
		message: "Ingress connection must match the conversation locator.",
		path: [
			"event",
			"locator",
			"connectionId"
		]
	});
});
function buildMessageChannelConversationId(locator) {
	if (locator.provider === "slack") return [
		locator.provider,
		locator.connectionId,
		locator.channelId,
		locator.rootMessageId
	].map(encodeConversationIdPart).join(":");
	return [
		locator.provider,
		locator.connectionId,
		locator.chatId,
		locator.rootMessageId
	].map(encodeConversationIdPart).join(":");
}
function encodeConversationIdPart(value) {
	return encodeURIComponent(value);
}
//#endregion
export { MESSAGE_CHANNEL_EVENT_KINDS, MESSAGE_CHANNEL_EVENT_TEXT_MAX_CHARS, MESSAGE_CHANNEL_MAX_RESOURCES, MESSAGE_CHANNEL_MENTION_TYPES, MESSAGE_CHANNEL_PROVIDERS, MESSAGE_CHANNEL_RESOURCE_TYPES, MESSAGE_CHANNEL_TRANSPORTS, buildMessageChannelConversationId, messageChannelCardControlSchema, messageChannelConversationLocatorSchema, messageChannelEventKindSchema, messageChannelIngressEnvelopeSchema, messageChannelMentionTypeSchema, messageChannelParticipantIdentitySchema, messageChannelProviderSchema, messageChannelResourceDescriptorSchema, messageChannelResourceTypeSchema, messageChannelTransportSchema, normalizedMessageChannelBotAddedEventSchema, normalizedMessageChannelCardActionEventSchema, normalizedMessageChannelDocumentCommentEventSchema, normalizedMessageChannelEventSchema, normalizedMessageChannelMessageEventSchema, normalizedMessageChannelReactionEventSchema, parseSlackThreadPublicationPermalink, slackMessagePermalinkSchema, slackThreadPublicationPermalinkSchema };

//# sourceMappingURL=message-channel.js.map